import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Tooltip,
    IconButton,
    Toolbar
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { LabelJournalLineViewModel, ListQuery } from "../../Types/ExportTypes";
import { formatNumber } from "../../Utils/NumberUtils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ImageBox from "../Image/ImageBox";
import LocalizedNumberInputs from "../Input/LocalizedNumberInputs"
import ResponsivePagination from "../ResponsivePagination";
interface LabelJournalLinesProps {
    documentNo: string;
    onLinesChanged?: () => void;
}

const LabelJournalLines: React.FC<LabelJournalLinesProps> = ({ documentNo, onLinesChanged }) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const { t } = useTranslation();

    const [lines, setLines] = useState<LabelJournalLineViewModel[]>([]);
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 20, filter: "", orderBy: "ext_ID", orderAsc: false });
    const [totalCount, setTotalCount] = useState(0);
    const [rowMode, setRowMode] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingLine, setEditingLine] = useState<LabelJournalLineViewModel | null>(null);

    const fetchLines = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authService.post<{ items: LabelJournalLineViewModel[]; count: number }>(`/labelJournalApi/GetLabelJournalLines?documentNo=${documentNo}`, query);
            setLines(response.items ?? []);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    }, [authService, documentNo, query, showSnackbar, t]);

    useEffect(() => {
        fetchLines();
    }, [fetchLines]);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const toggleRowMode = () => {
        const next = !rowMode;
        setRowMode(next);
        setQuery({ ...query, page: 0, pageSize: next ? 20 : 6 });
    };

    const openEditDialog = (line: LabelJournalLineViewModel) => {
        setEditingLine({ ...line });
        setIsEditOpen(true);
    };

    const closeEditDialog = () => {
        setIsEditOpen(false);
        setEditingLine(null);
    };

    const saveLine = async () => {
        if (!editingLine) return;
        try {
            showLoading();
            await authService.post(`/labelJournalApi/UpdateLine`, editingLine);
            closeEditDialog();
            fetchLines();
       
            showSnackbar(t("common.changesSaved"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const deleteLine = async (line: LabelJournalLineViewModel) => {
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        showLoading();
        try {
            await authService.post(`/labelJournalApi/DeleteLine?id=${line.id}`, {});
            fetchLines();
            
            showSnackbar(t("common.deleted"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.deleteError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const renderLineDetails = (line: LabelJournalLineViewModel) => (
        <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">{line.no}</Typography>
            <Typography variant="subtitle1">{line.description}</Typography>
            {line.barcode && <Typography variant="body2">{t("common.barcode")}: {line.barcode}</Typography>}
            {line.colourDescription && (
                <Typography variant="body2" color="text.secondary">
                    {line.colourDescription}{line.sizeDescription ? ` / ${line.sizeDescription}` : ""}{line.lengthDescription ? ` / ${line.lengthDescription}` : ""}
                </Typography>
            )}
            {line.shelfNo && <Typography variant="body2">{t("common.shelf")}: {line.shelfNo}</Typography>}
        </Stack>
    );

 


    return (
        <Box sx={{ width: "100%", mt: 2 }}>
            <Button variant="contained" color="primary" fullWidth onClick={toggleRowMode} sx={{ mb: 2 }}>
                {t("common.toggleListMode")}
            </Button>
            {rowMode ? (
                <Stack spacing={1}>
                    {lines.map((line, index) => (
                        <Paper key={line.id} elevation={0} sx={{ p: 2, backgroundColor: index % 2 === 0 ? "white" : "#fafafa" }}>
                            <Grid
                                container
                                spacing={1}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                                    ":hover": {
                                        backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                                    },
                                }}
                            >
                                <Grid item xs={12} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            maxWidth: "80px",
                                            maxHeight: "80px",
                                            height: "auto",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <ImageBox
                                            imageUrl={line.img_Url ?? ""}
                                            altText={line.description || "No Description"}
                                            fallbackText={t("common.noImage")}
                                            width="80px"
                                            height="80px"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    {renderLineDetails(line)}
                                </Grid>
                         
                                <Grid item xs={12} md={3}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2">{t("common.copies")}: {line.copies}</Typography>
                                        {line.unitPrice !== undefined && (
                                            <Typography variant="body2">{t("common.price")}: {formatNumber(line.unitPrice)}</Typography>
                                        )}
                                        {line.currencyCode && (
                                            <Typography variant="body2">{t("common.currency")}: {line.currencyCode}</Typography>
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={2}>
                         
                                  
                                            <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <Tooltip title={t("common.edit")}>
                                             <IconButton color="secondary" onClick={() => openEditDialog(line)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        
                                                <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            color="error"
                                                            onClick={async () => {
                                                                await deleteLine(line);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                         
                                        
                                  
                             
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Stack>
            ) : (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {lines.map((line) => (
                        <Grid item xs={12} md={6} key={line.id}>
                            <Card>
                                <CardContent>
                                    <Stack spacing={1}>
                                        <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={4}>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {line.no}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Tooltip title={t("common.edit")}>
                                                        <IconButton color="secondary" onClick={() => openEditDialog(line)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                           
                                                    <Grid item xs={4}>
                                                        <Tooltip title={t("common.delete")}>
                                                            <IconButton color="error" onClick={async () => {
                                                            await deleteLine(line);
                                                            }}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                             
                                            </Grid>
                                        </Toolbar>
                                        <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                    <ImageBox
                                                        imageUrl={line.img_Url ?? ""}
                                                        altText={line.description || "No Description"}
                                                        fallbackText={t("common.noImage")}
                                                        width="120px"
                                                        height="120px"

                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Typography variant="subtitle1">{line.description}</Typography>
                                        {line.barcode && <Typography variant="body2">{t("common.barcode")}: {line.barcode}</Typography>}
                                        <Typography variant="body2">{t("common.copies")}: {line.copies}</Typography>
                                        {line.unitPrice !== undefined && (
                                            <Typography variant="body2">{t("common.price")}: {formatNumber(line.unitPrice)}</Typography>
                                        )}
                                        {line.currencyCode && <Typography variant="body2">{t("common.currency")}: {line.currencyCode}</Typography>}
                                  
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ mt: 3 }}
            />

            <Dialog open={isEditOpen} onClose={closeEditDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t("common.edit")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={t("common.copies")}
                            type="number"
                            InputLabelProps={{ shrink: true }}
                            value={editingLine?.copies ?? 0}
                            onChange={(e) => setEditingLine(prev => prev ? { ...prev, copies: Number(e.target.value) } : prev)}
                            fullWidth
                        
                        />
                        <LocalizedNumberInputs
                            label={t("common.salesPrice")}
                            value={editingLine?.unitPrice ?? null}
                            onValueChange={(value) => setEditingLine(prev => prev ? { ...prev, unitPrice: value ?? 0 } : prev)}
               
                            allowNegative={true} // Prevents negative numbers
                            decimalScale={4} // Ensures max 4 decimals
                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog}>{t("common.cancel")}</Button>
                    <Button onClick={saveLine} variant="contained">{t("common.save")}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LabelJournalLines;